import { useState, useCallback, useRef, useEffect } from 'react';

interface UseResizableSidebarOptions<T> {
  minWidthPx?: number;
  maxWidthPx?: number;
  initialOpenSidebar?: T | null;
  storageKey?: string;
}

export interface SidebarControls<T> {
  openSidebar: T | null;
  isSidebarOpen: boolean;
  containerRef: React.MutableRefObject<HTMLDivElement | null>;
  toggleSidebar: (sidebar: T | null) => void;
}

/**
 * @deprecated This hook is deprecated and should not be used in new code.
 * It will be removed in a future version. Please use an alternative solution
 * or contact the development team for guidance on replacement patterns.
 *
 * Custom hook for managing resizable sidebar functionality
 * Provides responsive min/max width constraints based on pixel values
 * Persists sidebar state in localStorage to maintain user preferences
 *
 * @param options - Configuration options for the resizable sidebar
 * @param options.initialOpenSidebar - Initial open sidebar (default: null)
 * @param options.storageKey - Key for localStorage persistence (default: 'sidebar-state')
 * @returns Object containing sidebar state and control functions
 */
export function useResizableSidebar<T>({
  initialOpenSidebar = null,
  storageKey = 'sidebar-state',
}: UseResizableSidebarOptions<T> = {}): SidebarControls<T> {
  // Initialize state from localStorage or fallback to initial value
  const [openSidebar, setOpenSidebar] = useState<T | null>(() => {
    if (typeof window === 'undefined') {
      return initialOpenSidebar;
    }

    try {
      const stored = localStorage.getItem(storageKey);

      // If localStorage doesn't exist (first visit), use initialOpenSidebar
      if (stored === null) {
        return initialOpenSidebar;
      }

      // If localStorage exists and is false, don't show any sidebar
      const isOpen = JSON.parse(stored);
      if (isOpen === false) {
        return null;
      }

      // If localStorage exists and is true, use initialOpenSidebar
      return initialOpenSidebar;
    } catch (error) {
      console.warn('Failed to parse sidebar state from localStorage:', error);
      return initialOpenSidebar;
    }
  });

  const containerRef = useRef<HTMLDivElement>(null);

  // Save to localStorage whenever openSidebar changes
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      // Save boolean: true if sidebar is open, false if closed
      const isOpen = openSidebar !== null;
      localStorage.setItem(storageKey, JSON.stringify(isOpen));
    } catch (error) {
      console.warn('Failed to save sidebar state to localStorage:', error);
    }
  }, [openSidebar, storageKey]);

  // Function to toggle collapse state
  const toggleSidebar = useCallback((sidebar: T | null) => {
    setOpenSidebar(sidebar);
  }, []);

  const isSidebarOpen = openSidebar !== null;

  return {
    // State
    openSidebar,
    isSidebarOpen,
    containerRef,

    // Functions
    toggleSidebar,
  };
}
